import { useLayoutEffect, useRef } from 'react';

import {
  BILLING_BASE_URL,
  buyNowQueryParamsSessionStorage,
  INITIAL_BUYNOW_SEARCH_PARAMS,
  SECURE_BASE_URL,
  urlParams as urlParamsService,
} from '@ctct/infrastructure-layer';
import { useIsRedirectFromSecureEnabled } from '@ctct/state-server-layer';

// aff-app = awesome-feature-flag-apple-pay-payment :)
const APPLE_PAY_PARAM = 'aff-app';
const STRIPE_PHASE_2_PARAM = 'aff-st2';
const HIDE_PROMO_CODES_PARAM = 'aff-hpc';

export const isApplePayParamEnabled = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(APPLE_PAY_PARAM) === 'true';
};

export const isStripeParamEnabled = (): boolean => {
  return urlParamsService.getIfStripeTurnedOn();
};

export const isStripePhase2ParamEnabled = (): boolean => {
  return urlParamsService.getIfStripePhase2TurnedOn();
};

export const isHidePromoCodesParamEnabled = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(HIDE_PROMO_CODES_PARAM) === 'true';
};

const getSearchParamsWithFlags = ({
  isApplePayEnabled,
  isStripeEnabled,
  isHidePromoCodesEnabled,
  isStripePhase2Enabled,
  initialSearchParams,
}: {
  isApplePayEnabled?: boolean;
  isStripeEnabled?: boolean;
  isHidePromoCodesEnabled: boolean;
  isStripePhase2Enabled: boolean;
  initialSearchParams?: string;
}): string => {
  const searchParams = new URLSearchParams(initialSearchParams || window.location.search);

  buyNowQueryParamsSessionStorage.mergeIntoSearchParams(searchParams);

  // Remove the secure redirect param to prevent loops
  searchParams.delete('aff-sec-rdr');

  if (isApplePayEnabled) {
    searchParams.set(APPLE_PAY_PARAM, 'true');
  }

  if (isStripeEnabled) {
    searchParams.set(urlParamsService.getStripeFlagParamKey(), 'true');
  }

  if (isHidePromoCodesEnabled) {
    searchParams.set(HIDE_PROMO_CODES_PARAM, 'true');
  }

  if (isStripePhase2Enabled) {
    searchParams.set(STRIPE_PHASE_2_PARAM, 'true');
  }

  return searchParams.toString() ? `?${searchParams.toString()}` : '';
};

export const useSecureDomainRedirect = (
  path: string,
  areFlagsLoading: boolean,
  {
    isApplePayEnabled,
    isStripeEnabled,
    isStripePhase2Enabled,
    isHidePromoCodesEnabled,
  }: {
    isApplePayEnabled?: boolean;
    isStripeEnabled?: boolean;
    isHidePromoCodesEnabled: boolean;
    isStripePhase2Enabled: boolean;
  }
) => {
  const { data: isRedirectFromSecureEnabled, isLoading: isRedirectFromSecureLoading } =
    useIsRedirectFromSecureEnabled();

  const initialSearchParamsRef = useRef<string>(INITIAL_BUYNOW_SEARCH_PARAMS || window.location.search);

  useLayoutEffect(() => {
    buyNowQueryParamsSessionStorage.saveFromSearchParams(initialSearchParamsRef.current);
  }, []);

  useLayoutEffect(() => {
    const isLoading = areFlagsLoading || isRedirectFromSecureLoading;

    if (!isLoading) {
      const inputSearch = initialSearchParamsRef.current || '';
      const redirectSearch = getSearchParamsWithFlags({
        isApplePayEnabled,
        isStripeEnabled,
        isHidePromoCodesEnabled,
        isStripePhase2Enabled,
        initialSearchParams: inputSearch || undefined,
      });

      window.location.href = `${
        isRedirectFromSecureEnabled ? BILLING_BASE_URL : SECURE_BASE_URL
      }${path}${redirectSearch}`;
    }
  }, [
    isApplePayEnabled,
    areFlagsLoading,
    isStripeEnabled,
    path,
    isRedirectFromSecureEnabled,
    isRedirectFromSecureLoading,
    isHidePromoCodesEnabled,
    isStripePhase2Enabled,
  ]);
};
